import React from 'react'
import PropTypes from 'prop-types'
import equal from 'react-fast-compare'

import getConfig from 'next/config'
const {
  publicRuntimeConfig: { tocBoundary },
} = getConfig()

import style from './style'
import connect from 'store'
import { ArticleWithContent } from 'store/types'
import { renderArticleToc } from 'components/markdown'

export const TOCNode = ({
  text,
  slug,
  active = false,
  depth = 0,
  items = [],
  prefix = '',
  visibleHeadings,
}) => (
  <>
    {text && slug && (
      <a
        href={`#${slug}`}
        className={style('toc-link', `toc-link-${depth}`, active ? 'toc-link-active' : null)}
      >
        {text}
      </a>
    )}
    {items && items.length > 0 && (
      <ul className={style('toc-nav', `toc-nav-${depth}`)}>
        {items.map((item, i) => {
          const newPrefix = `${prefix}${prefix ? '-' : ''}${i}`
          active = !!visibleHeadings[item.slug]
          return (
            <li
              className={style(
                'toc-node',
                `toc-node-${item.depth}`,
                active ? 'toc-node-active' : null
              )}
              key={newPrefix}
            >
              <TOCNode
                prefix={newPrefix}
                active={active}
                {...item}
                visibleHeadings={visibleHeadings}
              />
            </li>
          )
        })}
      </ul>
    )}
  </>
)
TOCNode.propTypes = {
  prefix: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  slug: PropTypes.string,
  active: PropTypes.bool,
  depth: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      depth: PropTypes.number,
      title: PropTypes.string,
      slug: PropTypes.string,
    })
  ),
  visibleHeadings: PropTypes.object,
}

export class TOCTree extends React.Component {
  static propTypes = {
    topOffset: PropTypes.number,
    bottomOffset: PropTypes.number,
    id: PropTypes.string.isRequired,
    content: ArticleWithContent,
    title: PropTypes.string,
    children: PropTypes.node,
    Component: PropTypes.node,
    className: PropTypes.string,
  }
  Node = TOCNode

  constructor(props) {
    super(props)
    this.state = {
      tocTree: [],
      headings: [],
      visible: {},
      id: null,
    }
  }

  getVisibleElements(headings, elements) {
    const { topOffset: min = 0, bottomOffset = 0 } = this.props
    const max = window.innerHeight - bottomOffset
    const isVisible = (el, last) => {
      const { top } = el.getBoundingClientRect()
      const { bottom } = last.getBoundingClientRect()
      return (
        (top >= min && top < max) || // Top of element is within view
        (top < min && bottom > max) || // Element is bigger than view, and is in view.
        (bottom > min && bottom <= max) // Bottom of element is within view
      )
    }
    return headings
      .map((id) => [id, elements[id]])
      .filter(([id, [el, last]]) => !!el) // eslint-disable-line no-unused-vars
      .reduce(
        (obj, [id, [el, last]]) => ({
          ...obj,
          [id]: isVisible(el, last),
        }),
        {}
      )
  }

  checkVisibleElements() {
    const { tocHeadings, elements, visible: oldVisible, id } = this.state
    // If our ID has updated, we will reload our TOC.
    if (id !== this.props.id) return this.initializeTOC()
    // Do a simple check to see if the headings still exist. If not, reload our TOC
    if (tocHeadings.length >= 1 && !document.body.contains(elements[tocHeadings[0]][0])) {
      return this.initializeTOC()
    }
    // Otherwise, check if visibility has changed.
    const visible = this.getVisibleElements(tocHeadings, elements)
    if (!equal(oldVisible, visible)) this.setState({ ...this.state, visible })
  }

  getElements(headings) {
    const headingAndSiblings = (el) => {
      let last = el,
        next = null
      while (last.nextElementSibling) {
        next = last.nextElementSibling
        if (next.id) break
        last = next
      }
      return [el, last]
    }
    return headings.reduce(
      (obj, id) => ({ ...obj, [id]: headingAndSiblings(document.getElementById(id)) }),
      {}
    )
  }

  concatContent() {
    const {
      content: { content = [] },
    } = this.props
    if (!content.length) return ''
    return content.map(({ body }) => body).join('\n')
  }

  initializeTOC() {
    const { id, content } = this.props
    // const source = this.concatContent();
    const { tocTree, tocHeadings } = renderArticleToc(content)
    const elements = this.getElements(tocHeadings)
    const visible = this.getVisibleElements(tocHeadings, elements)
    this.setState({
      ...this.state,
      elements,
      tocTree,
      tocHeadings,
      visible,
      id,
    })
  }

  componentDidMount() {
    this.timer = setInterval(() => this.checkVisibleElements(), 100)
  }

  componentWillUnmount() {
    clearInterval(this.timer)
  }

  render() {
    const { tocTree, tocHeadings, visible } = this.state
    const { id, title, children, Component = this.Node, className = '' } = this.props
    if (!tocHeadings || !tocTree || tocHeadings.length < tocBoundary) {
      return <></>
    }
    return (
      <div className={style.combine(className, 'toc-tree', 'table-of-contents')}>
        {title && <h3>{title}</h3>}
        {children && children}
        <Component items={tocTree.items} visibleHeadings={visible} prefix={`toc-${id}`} />
      </div>
    )
  }
}

export default connect({
  extra: (
    { article: { articles: articleState, content: contentState }, category: categoryState },
    { articleId, id, categoryId }
  ) => {
    const article = articleState[articleId || id]
    const content = contentState[articleId || id]
    const category =
      categoryId || article ? categoryState[categoryId || article.category] : undefined
    return {
      article,
      content,
      category,
    }
  },
})(TOCTree)
